<template>
  <VeeForm
    ref="professionalCustomerFormRef"
    v-slot="{ handleSubmit }"
    :validation-schema="validationSchema"
    :initial-values="initialFormValues"
    as="div"
  >
    <form @submit="handleSubmit($event, onContinue)">
      <div class="mt-2 bg-white p-4 rounded-md">
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">Informations sur l'entreprise</p>
        <div class="grid grid-cols-2 gap-x-4 gap-y-3">
          <div class="flex-1">
            <label for="sirenNumber" class="block text-sm text-gray-700">
              SIREN
              <span class="ml-1 text-red-600">*</span>
            </label>
            <Field
              type="text"
              name="customer.sirenNumber"
              id="sirenNumber"
              autocomplete="off"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Numéro SIREN"
              v-model="localCustomer.sirenNumber"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="customer.sirenNumber" class="mt-2 text-sm text-red-600" />
          </div>

          <div class="flex-1">
            <label for="companyName" class="block text-sm text-gray-700">
              Nom de l'entreprise
              <span class="ml-1 text-red-600">*</span>
            </label>
            <Field
              type="text"
              name="customer.companyName"
              id="companyName"
              autocomplete="organization"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Estaly"
              v-model="localCustomer.companyName"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="customer.companyName" class="mt-2 text-sm text-red-600" />
          </div>

          <div class="flex-1">
            <label for="legalForm" class="block text-sm text-gray-700">
              Forme juridique
              <span class="ml-1 text-red-600">*</span>
            </label>
            <Field
              as="select"
              name="customer.legalForm"
              id="legalForm"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              v-model="localCustomer.legalForm"
            >
              <option value="">Sélectionner une forme juridique</option>
              <option value="sarl">SARL</option>
              <option value="eurl">EURL</option>
              <option value="sas">SAS</option>
              <option value="sasu">SASU</option>
              <option value="sa">SA</option>
              <option value="ei">EI</option>
              <option value="sca">SCA</option>
              <option value="scs">SCS</option>
              <option value="snc">SNC</option>
            </Field>
            <ErrorMessage name="customer.legalForm" class="mt-2 text-sm text-red-600" />
          </div>

          <!-- Address Fields -->
          <GoogleAddress
            class="flex-1"
            @newAddress="updateAddress"
            :addressError="effectiveAddressError"
            :initialAddress="addressDisplayValue"
          />

          <!-- Buyer's fields -->
          <div class="flex-1">
            <label for="firstName" class="block text-sm text-gray-700">
              Prénom du représentant
              <span class="ml-1 text-red-600">*</span>
            </label>
            <Field
              type="text"
              name="customer.firstName"
              id="firstName"
              autocomplete="given-name"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Jean"
              v-model="localCustomer.firstName"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="customer.firstName" class="mt-2 text-sm text-red-600" />
          </div>

          <div class="flex-1">
            <label for="lastName" class="block text-sm text-gray-700">
              Nom du représentant
              <span class="ml-1 text-red-600">*</span>
            </label>
            <Field
              type="text"
              name="customer.lastName"
              id="lastName"
              autocomplete="family-name"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Dupont"
              v-model="localCustomer.lastName"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="customer.lastName" class="mt-2 text-sm text-red-600" />
          </div>

          <div class="flex-1">
            <label for="email" class="block text-sm text-gray-700">
              {{ $t("Customer email") }}
              <span class="ml-1 text-red-600">*</span>
            </label>
            <Field
              id="email"
              name="customer.email"
              type="email"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="customer@gmail.com"
              v-model="localCustomer.email"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="customer.email" class="mt-2 text-sm text-red-600" />
          </div>

          <PhoneNumberFormField v-model="localCustomer.phone" :shipping-address="selectedStore?.shippingAddress" />

          <div class="flex-1">
            <label for="buyerRole" class="block text-sm text-gray-700">
              Fonction exercée par le représentant
              <span class="ml-1 text-red-600">*</span>
            </label>
            <Field
              type="text"
              name="customer.buyerRole"
              id="buyerRole"
              class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Directeur général"
              v-model="localCustomer.buyerRole"
              @keydown="blockEnterBehavior"
            />
            <ErrorMessage name="customer.buyerRole" class="mt-2 text-sm text-red-600" />
          </div>
        </div>

        <div class="mt-4 flex justify-end">
          <SecondaryButton type="submit">Continuer</SecondaryButton>
        </div>
      </div>
    </form>
  </VeeForm>
</template>

<script>
import * as yup from "yup";
import { Form as VeeForm, Field, ErrorMessage } from "vee-validate";
import GoogleAddress from "@/components/address/GoogleAddress";
import PhoneNumberFormField from "../form/customer_form/PhoneNumberFormField.vue";
import { SecondaryButton, phoneRegex } from "@estaly/ui";
import { mapGetters } from "vuex";
import { blockEnterBehavior } from "@/utils/blockEnterBehavior";

const professionalCustomerFormSchema = yup.object({
  customer: yup.object({
    email: yup.string().required("L'email est requis.").email("L'email doit être valide"),
    phone: yup
      .string()
      .required("Le numéro de téléphone est requis.")
      .matches(phoneRegex, "Le numéro de téléphone n'est pas valide."),
    firstName: yup
      .string()
      .required("Le prénom du client est requis.")
      .min(2, "Le prénom doit comporter au moins 2 caractères."),
    lastName: yup
      .string()
      .required("Le nom du client est requis.")
      .min(2, "Le nom doit comporter au moins 2 caractères."),
    sirenNumber: yup
      .string()
      .required("Le numéro de SIREN est requis")
      .length(9, "Le numéro de SIREN doit comporter 9 caractères"),
    companyName: yup.string().required("Le nom de l'entreprise est requis"),
    legalForm: yup.string().required("La forme juridique de l'entreprise est requise"),
    buyerRole: yup.string().required("Le rôle du représentant est requis"),
  }),
});

export default {
  components: {
    VeeForm,
    Field,
    ErrorMessage,
    GoogleAddress,
    PhoneNumberFormField,
    SecondaryButton,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    address: {
      type: Object,
      default: () => ({}),
    },
    addressError: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "newAddress", "continue"],
  data() {
    return {
      validationSchema: professionalCustomerFormSchema,
      addressValidationError: false,
    };
  },
  computed: {
    ...mapGetters("store", ["selectedStore"]),

    effectiveAddressError() {
      return this.addressError || this.addressValidationError;
    },

    addressDisplayValue() {
      const a = this.address;
      if (!a?.address1?.trim?.()) return "";
      const parts = [a.address1];
      if (a.zipCode || a.city) parts.push([a.zipCode, a.city].filter(Boolean).join(" "));
      if (a.country) parts.push(a.country);
      return parts.join(", ");
    },

    initialFormValues() {
      return {
        customer: this.modelValue || {},
      };
    },

    localCustomer: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  methods: {
    onContinue() {
      const hasAddress = this.address?.address1?.trim?.();
      if (!hasAddress) {
        this.addressValidationError = true;
        return;
      }
      this.addressValidationError = false;
      this.$emit("continue");
    },
    blockEnterBehavior,
    updateAddress(value) {
      this.$emit("newAddress", value);
    },
  },
  watch: {
    address: {
      deep: true,
      handler(newAddress) {
        if (!newAddress) return;
        if (newAddress?.address1?.trim?.()) {
          this.addressValidationError = false;
        }
      },
    },
  },
};
</script>
